import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { api, formatApiError, EMAIL_PATTERN } from "../../lib/api";
import { CONTACT_TOPICS } from "../../data/options";
import { Field, TextInput, TextArea, SelectInput, CheckRow, FormAlert, SubmissionSuccess } from "./FormKit";

const req = (label) => ({ required: `${label} is required` });

export const ContactForm = () => {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({ mode: "onTouched" });
  const [result, setResult] = useState(null);
  const [serverError, setServerError] = useState("");
  const inFlight = useRef(false);

  const onSubmit = async (values) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setServerError("");
    try {
      const { data } = await api.post("/contact", { ...values, organisation: values.organisation || null });
      setResult({ ...data, email: values.email });
    } catch (e) {
      setServerError(formatApiError(e));
    } finally {
      inFlight.current = false;
    }
  };

  if (result) {
    return (
      <SubmissionSuccess testId="contact-success" title="Message received"
        message="Thank you for contacting Trusted Leap. Your message has been received and a member of our team will respond as soon as practicable."
        reference={result.reference} emailStatus={result.confirmation_email} email={result.email} />
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6" data-testid="contact-form">
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" {...register("website")} />
      <div className="grid gap-6 md:grid-cols-2">
        <Field id="ct-full_name" label="Full name" required error={errors.full_name}>
          <TextInput id="ct-full_name" autoComplete="name" error={errors.full_name} data-testid="contact-name-input" {...register("full_name", req("Full name"))} />
        </Field>
        <Field id="ct-email" label="Email" required error={errors.email}>
          <TextInput id="ct-email" type="email" autoComplete="email" error={errors.email} data-testid="contact-email-input" {...register("email", { ...req("Email"), pattern: { value: EMAIL_PATTERN, message: "Enter a valid email address" } })} />
        </Field>
        <Field id="ct-organisation" label="Organisation">
          <TextInput id="ct-organisation" autoComplete="organization" data-testid="contact-organisation-input" {...register("organisation")} />
        </Field>
        <Field id="ct-topic" label="Topic" required error={errors.topic}>
          <SelectInput id="ct-topic" options={CONTACT_TOPICS} placeholder="Select topic" error={errors.topic} data-testid="contact-topic-select" {...register("topic", req("Topic"))} />
        </Field>
        <Field id="ct-subject" label="Subject" required error={errors.subject} className="md:col-span-2">
          <TextInput id="ct-subject" error={errors.subject} data-testid="contact-subject-input" {...register("subject", req("Subject"))} />
        </Field>
        <Field id="ct-message" label="Message" required error={errors.message} className="md:col-span-2">
          <TextArea id="ct-message" error={errors.message} data-testid="contact-message-input" {...register("message", { ...req("Message"), minLength: { value: 10, message: "Please provide at least 10 characters" } })} />
        </Field>
      </div>
      <CheckRow id="ct-privacy_ack" error={errors.privacy_ack} data-testid="contact-privacy-checkbox"
        label={<>I acknowledge the <Link to="/privacy-policy" target="_blank" className="text-[#2563EB] underline underline-offset-2">Privacy Policy</Link>.<span className="text-[#2563EB]"> *</span></>}
        {...register("privacy_ack", { required: "You must acknowledge the privacy notice" })} />
      <FormAlert message={serverError} testId="contact-server-error" />
      <button type="submit" disabled={isSubmitting} className="btn-primary" data-testid="contact-submit-button">
        {isSubmitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending</> : <>Send Message <ArrowRight className="h-4 w-4" /></>}
      </button>
    </form>
  );
};
